/**
 * Module-level cache for the app-wide singleton settings row. The
 * payload is tiny and changes rarely (admin-driven), so we share one
 * fetch across the whole app and invalidate on save.
 *
 * Follows the same pattern as `useRepairServiceTypes` — see that file
 * for the cancellation rationale (we don't tie the shared fetch to
 * any individual consumer's AbortSignal).
 *
 * Synchronous accessors mirror ``getHolidays()`` so non-React code
 * (e.g. ``utils/timeWindowResolver.ts`` consumed by ``data/pricingRules.ts``)
 * can read settings without prop-drilling. Call ``bootstrapAppSettings()``
 * from the app shell so the cache is warm before pricing rules first run.
 */

import { useEffect, useState } from 'react';
import type {
  AppSettings,
  BusinessHoursConfig,
  ProductCodeOption,
} from '@techgitter/contracts';
import {
  BUSINESS_HOURS_DEFAULT,
  PRODUCT_CODE_DEFAULTS,
} from '@techgitter/contracts';

import { fetchAppSettings } from '../services/appSettingsApi';

let cache: AppSettings | null = null;
let inflight: Promise<AppSettings> | null = null;
const subscribers = new Set<() => void>();

const getSettings = (): Promise<AppSettings> => {
  if (cache !== null) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchAppSettings()
      .then((s) => {
        cache = s;
        return s;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** Discard the cached settings and notify live consumers. Call after
 *  any successful PATCH on the admin page. */
export function invalidateAppSettingsCache(): void {
  cache = null;
  inflight = null;
  subscribers.forEach((notify) => {
    try {
      notify();
    } catch {
      /* keep other subscribers running on a single failure */
    }
  });
}

/** Kick off the initial fetch so the cache is warm by the time any
 *  module-level consumer (e.g. ``data/pricingRules.ts``) reads it.
 *  Call from the app entrypoint. Safe to call repeatedly. */
export function bootstrapAppSettings(): Promise<AppSettings> {
  return getSettings();
}

/** Synchronous read of the cached settings. Returns ``null`` when
 *  pre-bootstrap; callers must tolerate that and fall back to their
 *  own defaults (pattern matches ``getHolidays()``). */
export function getCachedAppSettings(): AppSettings | null {
  return cache;
}

/** Synchronous business-hours accessor. Returns ``BUSINESS_HOURS_DEFAULT``
 *  when the cache is cold OR the saved settings have ``businessHours: null``.
 *  Non-React code (``timeWindowResolver``) routes through this so a single
 *  admin save propagates to every consumer on the next call. */
export function getBusinessHours(): BusinessHoursConfig {
  return cache?.businessHours ?? BUSINESS_HOURS_DEFAULT;
}

/** Synchronous product-code catalog accessor. Returns
 *  ``PRODUCT_CODE_DEFAULTS`` when the cache is cold OR the saved
 *  settings have ``productCodes: null``. Used by quote dialogs to
 *  populate their dropdowns without needing per-render fetches. */
export function getProductCodes(): ProductCodeOption[] {
  return cache?.productCodes ?? PRODUCT_CODE_DEFAULTS;
}

export interface UseAppSettingsResult {
  settings: AppSettings | null;
  isLoading: boolean;
  error: string | null;
}

export function useAppSettings(): UseAppSettingsResult {
  const [settings, setSettings] = useState<AppSettings | null>(cache);
  const [isLoading, setIsLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setRev((r) => r + 1);
    subscribers.add(onInvalidate);
    return () => {
      subscribers.delete(onInvalidate);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(cache === null);
    getSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rev]);

  return { settings, isLoading, error };
}
